import { formatEuros } from "@bivak/domain";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { nl } from "../../copy/nl.js";
import { type AdminBookingRow, ApiError, fetchAdminBookings } from "../../lib/api.js";
import { asCents, formatDateRange } from "../../lib/format.js";

/**
 * SCHERM 7: the settlement queue. Stays that ended wait at the top; what is
 * already settled shows its balance, because "€ 136,35 teruggestort" is the
 * line the kassier actually comes here for.
 */
export function BeheerAfrekeningenPage() {
  const { data, isPending, isError } = useQuery({
    queryKey: ["admin-bookings"],
    queryFn: fetchAdminBookings,
    retry: (count, err) => (err instanceof ApiError && err.status < 500 ? false : count < 1),
  });

  if (isPending) return <p className="py-16 text-center text-mist">{nl.algemeen.laden}</p>;
  if (isError || !data) return <p className="py-16 text-center text-mist">{nl.algemeen.fout}</p>;

  const afTeRekenen = data.bookings.filter((row) => row.status === "stayed");
  const afgerekend = data.bookings
    .filter((row) => row.status === "settled" || row.status === "closed")
    .sort((a, b) => (a.arrivalDate < b.arrivalDate ? 1 : -1));

  return (
    <div>
      <h1 className="mb-5 font-bold font-display text-2xl tracking-tight">
        {nl.beheer.afrekeningen}
      </h1>
      {afTeRekenen.length === 0 && afgerekend.length === 0 && (
        <p className="text-mist">{nl.beheer.afrekening.leeg}</p>
      )}

      <Groep label={nl.beheer.afrekening.afTeRekenen} rows={afTeRekenen}>
        {() => (
          <span className="justify-self-end font-semibold text-[13px] text-rood">
            {nl.beheer.afrekening.rekenAf} →
          </span>
        )}
      </Groep>

      <Groep label={nl.beheer.afrekening.afgerekend} rows={afgerekend}>
        {(row) => (
          <span className="justify-self-end font-mono text-[12.5px]">
            {row.balanceCents === null ? (
              "—"
            ) : row.balanceCents === 0 ? (
              formatEuros(asCents(0))
            ) : (
              <>
                <span className="mr-2 font-sans text-[11px] text-mist">
                  {row.balanceCents > 0
                    ? nl.beheer.afrekening.terugTeStorten.toLowerCase()
                    : nl.beheer.afrekening.nogTeOntvangen.toLowerCase()}
                </span>
                {formatEuros(asCents(Math.abs(row.balanceCents)))}
              </>
            )}
          </span>
        )}
      </Groep>
    </div>
  );
}

function Groep({
  label,
  rows,
  children,
}: {
  label: string;
  rows: AdminBookingRow[];
  children: (row: AdminBookingRow) => React.ReactNode;
}) {
  if (rows.length === 0) return null;
  return (
    <section className="mb-6">
      <h2 className="mb-2 font-mono font-semibold text-[11px] text-rood uppercase tracking-widest">
        {label} · {rows.length}
      </h2>
      <div className="overflow-hidden rounded-xl border border-lijn bg-kaart">
        {rows.map((row) => (
          <Link
            key={row.id}
            to="/beheer/boekingen/$boekingId/afrekening"
            params={{ boekingId: row.id }}
            // Phone: name|action on the first line, dates under it — the
            // stacked row gave the balance a whole line to itself (user
            // feedback, 2026-08-21). Desktop keeps the aligned tracks.
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 border-lijn border-b px-4 py-2.5 last:border-b-0 hover:bg-zand/40 sm:grid-cols-[minmax(0,1fr)_11.5rem_minmax(0,14rem)] sm:gap-x-4 sm:gap-y-1 sm:py-3"
          >
            <span className="order-1 min-w-0 sm:order-none">
              <span className="block truncate font-semibold text-[13.5px]">{row.partyName}</span>
              {row.purpose && (
                <span className="block truncate text-mist text-xs">{row.purpose}</span>
              )}
            </span>
            <span className="order-3 col-span-2 truncate font-mono text-[12.5px] sm:order-none sm:col-span-1">
              {formatDateRange(row.arrivalDate, row.departureDate)}
            </span>
            <span className="order-2 justify-self-end sm:order-none sm:justify-self-auto">
              {children(row)}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
